import React, { useEffect, useMemo, useState } from 'react';
import { Shield, Plus, Edit3, Trash2, Save, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import permissionService from '../services/permissionService';
import toast from 'react-hot-toast';
import IconButton from '../components/IconButton';

const Permissoes = () => {
  const { user, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [groupForm, setGroupForm] = useState({ name: '', description: '', active: true });
  const [editingGroupId, setEditingGroupId] = useState(null);

  const selectedGroup = useMemo(() => groups.find(g => g.id === selectedGroupId) || null, [groups, selectedGroupId]);

  useEffect(() => {
    if (!isAuthenticated) return;
    const init = async () => {
      try {
        setLoading(true);
        const [appsRes, groupsRes] = await Promise.all([
          permissionService.listApplications(),
          permissionService.listGroups()
        ]);

        if (!appsRes.success || !groupsRes.success) {
          throw new Error(appsRes.message || groupsRes.message || 'Erro ao carregar dados');
        }

        setApplications(appsRes.data || []);
        setGroups(groupsRes.data || []);
        const firstGroupId = (groupsRes.data && groupsRes.data[0]?.id) || null;
        setSelectedGroupId(firstGroupId);

        if (firstGroupId) {
          const permRes = await permissionService.getGroupPermissions(firstGroupId);
          if (permRes.success) {
            const m = {};
            for (const row of (permRes.data || [])) {
              m[row.applicationId] = {
                canView: !!row.canView,
                canCreate: !!row.canCreate,
                canEdit: !!row.canEdit,
                canDelete: !!row.canDelete,
                canExport: !!row.canExport,
              };
            }
            setMatrix(m);
          }
        }
      } catch (err) {
        console.error(err);
        toast.error(err.message || 'Erro ao inicializar permissões');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [isAuthenticated]);

  const handleSelectGroup = async (id) => {
    setSelectedGroupId(id);
    try {
      setLoading(true);
      const permRes = await permissionService.getGroupPermissions(id);
      if (permRes.success) {
        const m = {};
        for (const row of (permRes.data || [])) {
          m[row.applicationId] = {
            canView: !!row.canView,
            canCreate: !!row.canCreate,
            canEdit: !!row.canEdit,
            canDelete: !!row.canDelete,
            canExport: !!row.canExport,
          };
        }
        setMatrix(m);
      }
    } catch (err) {
      toast.error('Erro ao carregar permissões do grupo');
    } finally {
      setLoading(false);
    }
  };

  const togglePerm = (applicationId, field) => {
    setMatrix(prev => ({
      ...prev,
      [applicationId]: {
        canView: prev[applicationId]?.canView || false,
        canCreate: prev[applicationId]?.canCreate || false,
        canEdit: prev[applicationId]?.canEdit || false,
        canDelete: prev[applicationId]?.canDelete || false,
        canExport: prev[applicationId]?.canExport || false,
        [field]: !prev[applicationId]?.[field]
      }
    }));
  };

  const handleSave = async () => {
    if (!selectedGroupId) return;
    try {
      setLoading(true);
      const permissions = applications.map(app => ({
        applicationId: app.id,
        canView: matrix[app.id]?.canView || false,
        canCreate: matrix[app.id]?.canCreate || false,
        canEdit: matrix[app.id]?.canEdit || false,
        canDelete: matrix[app.id]?.canDelete || false,
        canExport: matrix[app.id]?.canExport || false,
      }));
      const res = await permissionService.updateGroupPermissions(selectedGroupId, permissions);
      if (res.success) {
        toast.success('Permissões salvas com sucesso');
      } else {
        toast.error(res.message || 'Falha ao salvar permissões');
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar permissões');
    } finally {
      setLoading(false);
    }
  };

  const startCreateGroup = () => {
    setEditingGroupId(null);
    setGroupForm({ name: '', description: '', active: true });
  };

  const startEditGroup = (g) => {
    setEditingGroupId(g.id);
    setGroupForm({ name: g.name, description: g.description || '', active: !!g.active });
  };

  const submitGroupForm = async () => {
    try {
      setLoading(true);
      if (editingGroupId) {
        const res = await permissionService.updateGroup(editingGroupId, groupForm);
        if (res.success) {
          toast.success('Grupo atualizado');
        } else {
          toast.error(res.message || 'Erro ao atualizar grupo');
        }
      } else {
        const res = await permissionService.createGroup(groupForm);
        if (res.success) {
          toast.success('Grupo criado');
        } else {
          toast.error(res.message || 'Erro ao criar grupo');
        }
      }
      const groupsRes = await permissionService.listGroups();
      if (groupsRes.success) setGroups(groupsRes.data || []);
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar grupo');
    } finally {
      setLoading(false);
    }
  };

  const deleteGroup = async (id) => {
    if (!confirm('Tem certeza que deseja excluir este grupo?')) return;
    try {
      setLoading(true);
      const res = await permissionService.deleteGroup(id);
      if (res.success) {
        toast.success('Grupo excluído');
        const groupsRes = await permissionService.listGroups();
        if (groupsRes.success) setGroups(groupsRes.data || []);
        if (selectedGroupId === id) setSelectedGroupId(groupsRes.data?.[0]?.id || null);
      } else {
        toast.error(res.message || 'Erro ao excluir grupo');
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao excluir grupo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center mb-4">
        <Shield className="w-5 h-5 text-primary-600 mr-2" />
        <h1 className="text-lg font-semibold">Permissões por Grupo</h1>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Grupos */}
        <div className="col-span-3 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium">Grupos</h2>
            <IconButton
              icon={Plus}
              onClick={startCreateGroup}
              variant="primary"
              size="sm"
              title="Novo Grupo"
            />
          </div>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {groups.map(g => (
              <div key={g.id} className={`border rounded p-2 ${selectedGroupId === g.id ? 'border-primary-500' : 'border-gray-200 dark:border-gray-700'}`}>
                <div className="flex items-center justify-between">
                  <button
                    className="text-left text-sm font-medium text-gray-800 dark:text-gray-100"
                    onClick={() => handleSelectGroup(g.id)}
                  >
                    {g.name}
                  </button>
                  <div className="flex items-center space-x-2">
                    <IconButton
                      icon={Edit3}
                      onClick={() => startEditGroup(g)}
                      variant="warning"
                      size="sm"
                      title="Editar grupo"
                    />
                    <IconButton
                      icon={Trash2}
                      onClick={() => deleteGroup(g.id)}
                      variant="danger"
                      size="sm"
                      title="Excluir grupo"
                    />
                  </div>
                </div>
              </div>
            ))}
            {groups.length === 0 && (
              <div className="text-xs text-gray-500">Nenhum grupo cadastrado.</div>
            )}
          </div>
          {/* Formulário de grupo */}
          <div className="mt-4 border-t pt-3">
            <h3 className="text-xs font-semibold mb-2">{editingGroupId ? 'Editar Grupo' : 'Novo Grupo'}</h3>
            <div className="space-y-2">
              <input
                type="text"
                className="w-full border rounded px-2 py-1 text-sm"
                placeholder="Nome do grupo"
                value={groupForm.name}
                onChange={e => setGroupForm({ ...groupForm, name: e.target.value })}
              />
              <input
                type="text"
                className="w-full border rounded px-2 py-1 text-sm"
                placeholder="Descrição (opcional)"
                value={groupForm.description}
                onChange={e => setGroupForm({ ...groupForm, description: e.target.value })}
              />
              <label className="flex items-center space-x-2 text-xs">
                <input
                  type="checkbox"
                  checked={groupForm.active}
                  onChange={e => setGroupForm({ ...groupForm, active: e.target.checked })}
                />
                <span>Ativo</span>
              </label>
              <IconButton
                icon={Save}
                onClick={submitGroupForm}
                variant="primary"
                size="sm"
                title="Salvar Grupo"
              />
            </div>
          </div>
        </div>

        {/* Matriz de Permissões */}
        <div className="col-span-9 bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium">Permissões para: {selectedGroup?.name || '—'}</h2>
            <div className="flex items-center space-x-2">
              <IconButton
                icon={RefreshCw}
                onClick={() => handleSelectGroup(selectedGroupId)}
                variant="secondary"
                size="sm"
                title="Recarregar permissões"
              />
              <IconButton
                icon={Save}
                onClick={handleSave}
                variant="primary"
                size="sm"
                title="Salvar Permissões"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="px-3 py-2">Aplicação</th>
                  <th className="px-3 py-2">Ver</th>
                  <th className="px-3 py-2">Criar</th>
                  <th className="px-3 py-2">Editar</th>
                  <th className="px-3 py-2">Excluir</th>
                  <th className="px-3 py-2">Exportar</th>
                </tr>
              </thead>
              <tbody>
                {applications.map(app => {
                  const perms = matrix[app.id] || {};
                  const Cell = ({ field }) => (
                    <td className="px-3 py-2">
                      <label className="inline-flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={!!perms[field]}
                          onChange={() => togglePerm(app.id, field)}
                          disabled={!selectedGroupId || loading}
                        />
                      </label>
                    </td>
                  );

                  return (
                    <tr key={app.id} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="px-3 py-2 font-medium text-gray-800 dark:text-gray-100">{app.name}</td>
                      <Cell field="canView" />
                      <Cell field="canCreate" />
                      <Cell field="canEdit" />
                      <Cell field="canDelete" />
                      <Cell field="canExport" />
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {!selectedGroupId && (
            <div className="mt-4 flex items-center text-xs text-gray-500">
              <XCircle className="w-3 h-3 mr-1" /> Selecione um grupo para editar as permissões.
            </div>
          )}
          {selectedGroupId && (
            <div className="mt-4 flex items-center text-xs text-green-600">
              <CheckCircle className="w-3 h-3 mr-1" /> Alterações locais não são salvas automaticamente — clique em "Salvar Permissões".
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-xs text-gray-500">Carregando...</div>
      )}
    </div>
  );
};

export default Permissoes;